import { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { I } from '../../../../common/v5/config';
import {
  saveWorkflow,
  saveWorkflowAsNew,
  updateWorkflow,
  loadWorkflows,
  setUnsavedChanges,
  setCurrentWorkflow
} from '../../../../redux/actions/async/agentBuilder';

export const useWorkflowSaving = (areaList = []) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const params = useParams();
  
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const autoSaveTimeoutRef = useRef(null);
  
  // Get state from Redux
  const currentWorkflow = useSelector(state => state.app?.agentBuilder?.currentWorkflow || null);
  const hasUnsavedChanges = useSelector(state => state.app?.agentBuilder?.hasUnsavedChanges || false);
  const nodes = useSelector(state => state.app?.agentBuilder?.nodes || []);
  const connections = useSelector(state => state.app?.agentBuilder?.connections || []);
  const workflowSettings = useSelector(state => state.app?.agentBuilder?.workflowSettings || {});
  const workflowMeta = useSelector(state => state.app?.agentBuilder?.workflowMeta || {});
  
  const isNewWorkflow = !currentWorkflow?.workflowId || currentWorkflow?.workflowId === 'new' || params.id === 'new';
  const isDirty = hasUnsavedChanges;

  // Initialize current workflow if not set and we have a params.id
  useEffect(() => {
    if (!currentWorkflow && params.id && params.id !== 'new') {
      dispatch(setCurrentWorkflow({ workflowId: params.id }));
    }
  }, [currentWorkflow, params.id, dispatch]);

  // Generate a unique workflow ID for new workflows
  const generateWorkflowId = useCallback(() => {
    return `workflow_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Get workflow data to save
  const getWorkflowDataToSave = useCallback(() => {
    console.log('getWorkflowDataToSave - Redux nodes:', nodes);
    console.log('getWorkflowDataToSave - Redux connections:', connections);
    console.log('getWorkflowDataToSave - currentWorkflow:', currentWorkflow);
    console.log('getWorkflowDataToSave - areaList:', areaList);
    
    // For new workflows, create a basic workflow object
    const workflowData = currentWorkflow || {
      workflowId: generateWorkflowId(),
      name: 'New Workflow',
      description: '',
      active: true,
      systemGroup: 'default',
      area: null, // Will be set below
      spc: 'default'
    };

    console.log('getWorkflowDataToSave - workflowData.active type:', typeof workflowData.active, 'value:', workflowData.active);
    
    // Determine area value
    let areaValue = 1; // fallback to area ID 1
    if (workflowData.area) {
      // Existing workflow has area - use it (as integer since backend now expects int)
      areaValue = parseInt(workflowData.area, 10);
    } else if (areaList && areaList.length > 0) {
      // No existing area, use first available area ID
      areaValue = areaList[0].id;
      console.log('getWorkflowDataToSave - using first available area ID:', areaValue);
    }
    
    const dataToSave = {
      workflowId: workflowData.workflowId,
      name: workflowData.name || 'Untitled Workflow',
      description: workflowData.description || '',
      active: workflowData.active !== undefined ? Boolean(workflowData.active) : true,
      nodes: JSON.stringify(nodes),
      connections: JSON.stringify(connections),
      settings: JSON.stringify(workflowSettings),
      meta: JSON.stringify({
        ...workflowMeta,
        canvasPosition: workflowMeta.canvasPosition || { x: 0, y: 0 },
        zoom: workflowMeta.zoom || 1,
        lastSaved: new Date().toISOString()
      }),
      tags: workflowData.tags || [],
      systemGroup: workflowData.systemGroup || 'default',
      area: areaValue,
      spc: 'default' // Backend will override this with actual spc from context
    };
    
    console.log('getWorkflowDataToSave - dataToSave.active type:', typeof dataToSave.active, 'value:', dataToSave.active);
    console.log('getWorkflowDataToSave - dataToSave.area:', dataToSave.area);
    
    console.log('getWorkflowDataToSave - final data:', dataToSave);
    return dataToSave;
  }, [currentWorkflow, nodes, connections, workflowSettings, workflowMeta, generateWorkflowId, areaList]);

  // Save as new workflow
  const saveAsNewWorkflow = useCallback(async ({ name, description, tags, area } = {}) => {
    try {
      setIsSaving(true);
      setSaveError(null);

      const workflowData = getWorkflowDataToSave();

      // Generate new workflow ID
      const newWorkflowId = generateWorkflowId();
      
      const newWorkflowData = {
        ...workflowData,
        workflowId: newWorkflowId,
        name: name?.trim() || workflowData.name,
        description: description?.trim() || workflowData.description,
        tags: tags || workflowData.tags,
        area: area || workflowData.area
      };

      const savedWorkflow = await dispatch(saveWorkflowAsNew(newWorkflowData));
      
      if (savedWorkflow && savedWorkflow.error) {
        throw new Error(savedWorkflow.error);
      }
      
      if (savedWorkflow) {
        // Update current workflow state
        dispatch(setCurrentWorkflow(savedWorkflow));
        dispatch(setUnsavedChanges(false));
        setSaveError(null); // Clear any previous errors
        // Refresh workflow list so Load picker shows the new workflow (and first save stays visible after New)
        dispatch(loadWorkflows());
        return savedWorkflow;
      }

      return null;
    } catch (error) {
      console.error('Error saving new workflow:', error);
      setSaveError(error.message || 'Failed to save workflow');
      
      // Show error toast notification
      dispatch({
        type: 'ADD_TOAST_ALERT',
        payload: {
          type: { class: 'danger', icon: 'icon-alert' },
          position: 'top-right',
          text: `Failed to save workflow: ${error.message || 'Unknown error'}`
        }
      });
      
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, generateWorkflowId, getWorkflowDataToSave, history]);

  // Update existing workflow
  const updateExistingWorkflow = useCallback(async ({ name, description, tags } = {}) => {
    try {
      setIsSaving(true);
      setSaveError(null);

      const workflowData = getWorkflowDataToSave();

      const updateData = {
        ...workflowData,
        name: name?.trim() || workflowData.name,
        description: description?.trim() || workflowData.description,
        tags: tags || workflowData.tags
      };

      const updatedWorkflow = await dispatch(updateWorkflow(workflowData.workflowId, updateData));
      
      // Check if the result contains an error even with 200 status
      if (updatedWorkflow && updatedWorkflow.error) {
        throw new Error(updatedWorkflow.error);
      }
      
      if (updatedWorkflow) {
        dispatch(setCurrentWorkflow(updatedWorkflow));
        dispatch(setUnsavedChanges(false));
        setSaveError(null); // Clear any previous errors
        dispatch(loadWorkflows());
        return updatedWorkflow;
      }

      return null;
    } catch (error) {
      console.error('Error updating workflow:', error);
      setSaveError(error.message || 'Failed to update workflow');
      
      // Show error toast notification
      dispatch({
        type: 'ADD_TOAST_ALERT',
        payload: {
          type: { class: 'danger', icon: 'icon-alert' },
          position: 'top-right',
          text: `Failed to update workflow: ${error.message || 'Unknown error'}`
        }
      });
      
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [dispatch, getWorkflowDataToSave]);

  // Save current workflow (handles both new and existing)
  const saveCurrentWorkflow = useCallback(async (options = {}) => {
    try {
      if (isNewWorkflow) {
        return await saveAsNewWorkflow(options);
      } else {
        return await updateExistingWorkflow(options);
      }
    } catch (error) {
      console.error('Error saving workflow:', error);
      setSaveError(error.message || 'Failed to save workflow');
      
      // Show error toast notification
      dispatch({
        type: 'ADD_TOAST_ALERT',
        payload: {
          type: { class: 'danger', icon: 'icon-alert' },
          position: 'top-right',
          text: `Failed to save workflow: ${error.message || 'Unknown error'}`
        }
      });
      
      throw error; // Re-throw so calling components can handle it
    }
  }, [isNewWorkflow, saveAsNewWorkflow, updateExistingWorkflow, dispatch]);

  // Auto-save functionality
  const startAutoSave = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    autoSaveTimeoutRef.current = setTimeout(async () => {
      if (isDirty && !isNewWorkflow) {
        try {
          await saveCurrentWorkflow();
          console.log('Auto-saved workflow');
        } catch (error) {
          console.warn('Auto-save failed:', error);
        }
      }
    }, 5000); // Auto-save after 5 seconds of inactivity
  }, [isDirty, isNewWorkflow, saveCurrentWorkflow]);

  const stopAutoSave = useCallback(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }
  }, []);

  // Prompt save unsaved changes
  const promptSaveUnsavedChanges = useCallback(async () => {
    if (!isDirty) {
      return true;
    }

    const userChoice = window.confirm(
      I('You have unsaved changes. Do you want to save them before leaving?')
    );

    if (userChoice) {
      try {
        const saved = await saveCurrentWorkflow();
        return !!saved;
      } catch (error) {
        console.error('Error saving workflow before navigation:', error);
        return false;
      }
    }

    return true; // User chose not to save
  }, [isDirty, saveCurrentWorkflow]);

  // Duplicate workflow
  const duplicateWorkflow = useCallback(async (workflowToDuplicate) => {
    try {
      const duplicatedData = {
        ...workflowToDuplicate,
        workflowId: generateWorkflowId(),
        name: `${workflowToDuplicate.name} (Copy)`,
        active: false // New workflows should start inactive
      };

      return await dispatch(saveWorkflowAsNew(duplicatedData));
    } catch (error) {
      console.error('Error duplicating workflow:', error);
      throw error;
    }
  }, [dispatch, generateWorkflowId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAutoSave();
    };
  }, [stopAutoSave]);

  return {
    // State
    isSaving,
    saveError,
    isNewWorkflow,
    isDirty,
    currentWorkflow,
    
    // Actions
    saveCurrentWorkflow,
    saveAsNewWorkflow,
    updateExistingWorkflow,
    duplicateWorkflow,
    startAutoSave,
    stopAutoSave,
    promptSaveUnsavedChanges,
    clearSaveError: () => setSaveError(null),
    
    // Helpers
    getWorkflowDataToSave
  };
};